import { useEffect } from 'react';

/**
 * useClickOutside
 *
 * This hook detects clicks outside the referenced element and triggers a callback function.
 * It's useful for closing modals, dropdowns, or tooltips when the user clicks outside the component.
 *
 * @template T - Type of the HTML element being referenced.
 * @param ref - A React ref pointing to the element to monitor for outside clicks.
 * @param onClickOutside - Callback function to execute when a click outside the element is detected.
 *
 * @example
 * const ref = useRef<HTMLDivElement>(null);
 * useClickOutside(ref, () => setIsOpen(false));
 */
export function useClickOutside<T extends HTMLElement>(
    ref: React.RefObject<T | null>,
    onClickOutside: () => void
): void {
    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                onClickOutside();
            }
        };

        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [ref, onClickOutside]);
}
